import { Fragment, useState } from 'react';
import { modules as ModulesApi, sections as SectionsApi, type ModuleItem, type ModuleSwitches as Switches, type SectionMode } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';
import { NewsSections } from './NewsSections';

type Field = 'app' | 'site' | 'android' | 'ios';

/* The parents sub-modules sit under, in the order they are listed. */
const GROUPS = [
  { key: 'learning', label: 'Learning', blurb: 'Recorded courses and live classes.' },
  { key: 'landing', label: 'Landing page', blurb: 'Optional bands on the home page.' },
];

/**
 * The switchboard for the client's modules and sub-modules.
 *
 * Two switches per sub-module, because the website and the working app are
 * different audiences: a module can be finished inside the app long before it
 * is ready to advertise, or kept on the site while the desk stops using it.
 *
 * What "off" does is spelled out next to each switch, so nobody flips one to
 * tidy a menu and discovers afterwards that they closed a member-facing page.
 *
 * Off does not have to mean gone: a module the desk now runs on Telegram can
 * carry that link, and members, the phone apps and the website send people
 * there instead of showing nothing.
 */
export function Modules() {
  const { data, error, reload } = useAsync((signal) => ModulesApi.load(signal));
  const [edit, setEdit] = useState<Record<string, Switches> | null>(null);
  const [secEdit, setSecEdit] = useState<Record<string, SectionMode>>({});
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState('');
  const [saveError, setSaveError] = useState('');

  if (error) return <div className="pg"><div className="err">{String((error as Error)?.message ?? error)}</div></div>;
  if (!data) return <SkeletonPage />;

  const state = edit ?? data.state;
  const dirty = (!!edit && JSON.stringify(edit) !== JSON.stringify(data.state)) || Object.keys(secEdit).length > 0;
  const get = (key: string): Switches => state[key] ?? { app: true, site: true, android: true, ios: true };
  const flip = (key: string, field: Field) => {
    const cur = get(key);
    setEdit({ ...state, [key]: { ...cur, [field]: !(cur[field] ?? cur.app) } });
  };

  async function save() {
    setSaveError('');
    setStatus('Saving…');
    try {
      if (Object.keys(secEdit).length) await SectionsApi.save(secEdit);
      if (edit) await ModulesApi.save(state);
      setStatus('Saved — reloading so the sidebar matches');
      /* Screens come and go with permissions, which are read at sign-in time
         by the rest of the console; a reload is the honest way to show it. */
      window.setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      setStatus('');
      setSaveError((e as Error).message);
      await reload();
    }
  }

  const Switch = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="sw"
      {...(on ? { 'data-on': '' } : {})}
      onClick={onClick}
      style={{ border: 0, padding: 0 }}
    />
  );

  const groups = GROUPS.map((g) => ({ ...g, items: data.modules.filter((m) => m.parent === g.key) }))
    .filter((g) => g.items.length > 0);
  const otherModules = data.modules.filter((m) => !m.parent || !GROUPS.some((g) => g.key === m.parent));

  /* One switch and its word. A phone column nobody has set follows the web
     app's, which is what the server does too. */
  const cell = (m: ModuleItem, s: Switches, field: Field, where: string) => {
    const on = s[field] ?? s.app;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch on={on} label={`${m.label} ${where}`} onClick={() => flip(m.key, field)} />
        <span style={{ fontSize: 12.5, color: on ? 'var(--tx)' : 'var(--tx3)' }}>{on ? 'On' : 'Off'}</span>
      </div>
    );
  };
  const phoneCell = (m: ModuleItem, s: Switches, field: 'android' | 'ios', where: string) => (m.siteOnly
    ? <span className="muted" style={{ fontSize: 12.5 }}>—</span>
    : cell(m, s, field, where));

  /* A website-only module has nothing in the member area to switch, so its
     first cell says so instead of offering a switch that would do nothing. */
  const appCell = (m: ModuleItem, s: Switches) => (m.siteOnly
    ? <span className="muted" style={{ fontSize: 12.5 }}>Website only</span>
    : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch on={s.app} label={`${m.label} in the admin and member area`} onClick={() => flip(m.key, 'app')} />
        <span style={{ fontSize: 12.5, color: s.app ? 'var(--tx)' : 'var(--tx3)' }}>{s.app ? 'On' : 'Off'}</span>
      </div>
    ));

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Modules</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7, maxWidth: 720, lineHeight: 1.55 }}>
            Tradevix runs as modular components. Switch one off and it disappears everywhere in that place —
            its sidebar screens, its notifications, its dashboard cards and its search results — and its
            pages stop answering. Members, plans, billing and settings always stay on.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <button className="b bo" type="button" disabled={!dirty} onClick={() => { setEdit(null); setSecEdit({}); }}>Undo</button>
          <button className="b bg" type="button" disabled={!dirty} onClick={() => void save()}>Save</button>
        </div>
      </div>

      {saveError ? <div className="err">{saveError}</div> : null}

      <div className="crd" style={{ padding: "14px 18px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(var(--goldrgb), .06)", borderColor: "rgba(var(--goldrgb), .25)" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--tx)" }}>Telegram &amp; WhatsApp Home Icons (ON / OFF)</div>
          <div style={{ fontSize: 13, color: "var(--tx2)", marginTop: 2 }}>
            Turn ON or OFF the Telegram &amp; WhatsApp icons on the Mobile App &amp; Website Home screens, and edit their links.
          </div>
        </div>
        <a href="/settings" className="b bg" style={{ fontSize: 12.5, padding: "7px 14px", textDecoration: "none" }}>
          Configure in Contact &amp; Settings →
        </a>
      </div>

      <NewsSections edits={secEdit} onEdit={setSecEdit} />

      <div className="crd" style={{ padding: "6px 22px" }}>
        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <th>Module</th>
                <th style={{ width: 190 }}>Admin &amp; member area</th>
                <th style={{ width: 130 }}>Android app</th>
                <th style={{ width: 130 }}>iOS app</th>
                <th style={{ width: 130 }}>Website</th>
              </tr>
            </thead>
            <tbody>
              {/* Parent categories and their sub-modules */}
              {groups.map((g) => {
                const open = !closed[g.key];
                return (
                  <Fragment key={g.key}>
                    <tr
                      style={{ background: 'rgba(var(--wash), calc(.015 * var(--washx)))', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setClosed((c) => ({ ...c, [g.key]: open }))}
                    >
                      <td colSpan={5} style={{ padding: '16px 8px 10px', borderBottom: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            type="button"
                            aria-label={open ? `Collapse ${g.label}` : `Expand ${g.label}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--tx2)',
                              padding: 0,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {open ? <LuChevronDown size={17} /> : <LuChevronRight size={17} />}
                          </button>
                          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--tx)' }}>{g.label}</span>
                        </div>
                        <div className="muted" style={{ fontSize: 12.5, marginTop: 3, marginLeft: 25 }}>
                          {g.blurb}
                        </div>
                      </td>
                    </tr>
                    {open && g.items.map((m, idx) => {
                      const s = get(m.key);
                      const isLast = idx === g.items.length - 1;
                      const line = isLast ? undefined : '1px solid rgba(var(--wash), calc(.03 * var(--washx)))';
                      return (
                        <tr key={m.key} style={{ background: 'rgba(var(--wash), calc(.015 * var(--washx)))' }}>
                          <td style={{ padding: '12px 8px 14px 33px', borderBottom: line }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                              <span style={{ color: 'var(--tx3)', fontSize: 13, marginTop: 1, fontFamily: 'monospace' }}>
                                {isLast ? '└──' : '├──'}
                              </span>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--tx)' }}>{m.label}</div>
                                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{m.blurb}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ borderBottom: line }}>{appCell(m, s)}</td>
                          <td style={{ borderBottom: line }}>{phoneCell(m, s, 'android', 'in the Android app')}</td>
                          <td style={{ borderBottom: line }}>{phoneCell(m, s, 'ios', 'in the iOS app')}</td>
                          <td style={{ borderBottom: line }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Switch on={s.site} label={`${m.label} on the website`} onClick={() => flip(m.key, 'site')} />
                              <span style={{ fontSize: 12.5, color: s.site ? 'var(--tx)' : 'var(--tx3)' }}>{s.site ? 'On' : 'Off'}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}

              {/* Other top-level modules */}
              {otherModules.map((m) => {
                const s = get(m.key);
                return (
                  <tr key={m.key}>
                    <td style={{ padding: '16px 8px' }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5 }}>{m.label}</div>
                      <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>{m.blurb}</div>
                    </td>
                    <td>{appCell(m, s)}</td>
                    <td>{phoneCell(m, s, 'android', 'in the Android app')}</td>
                    <td>{phoneCell(m, s, 'ios', 'in the iOS app')}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Switch on={s.site} label={`${m.label} on the website`} onClick={() => flip(m.key, 'site')} />
                        <span style={{ fontSize: 12.5, color: s.site ? 'var(--tx)' : 'var(--tx3)' }}>{s.site ? 'On' : 'Off'}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      <p className="muted" style={{ fontSize: 12, marginTop: 14, lineHeight: 1.6, maxWidth: 720 }}>
        The Android and iOS columns apply to the phone apps; until one is changed it follows the
        admin &amp; member area column. A module switched off with a link keeps its place in the
        member area's menu and shows a card with an Open button instead of its screens; the website
        sends its pages straight to the link. Nothing is deleted when a module is off — its calls, articles and courses stay in the database and
        come back exactly as they were when it is switched on again. Every change is written to the audit trail.
      </p>
    </div>
  );
}

